import { supabase } from '@/lib/supabase';
import type {
  BillingCycle,
  BillingStatusResponse,
  CreateCheckoutResponse,
  CreateCustomerResponse,
  ManageSubscriptionResponse,
  SubscriptionPlan,
  BillingError,
} from '@/types';

function isError(data: unknown): data is BillingError {
  return typeof data === 'object' && data !== null && 'error' in data;
}

export async function createCustomer(
  projectId: string,
): Promise<{ data: CreateCustomerResponse | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(
    'billing-create-customer',
    { body: { project_id: projectId } },
  );

  if (error) return { data: null, error: error.message };
  if (isError(data)) return { data: null, error: data.error };
  return { data: data as CreateCustomerResponse, error: null };
}

export async function createCheckout(
  projectId: string,
  plan: Exclude<SubscriptionPlan, 'free'>,
  billingCycle: BillingCycle = 'monthly',
): Promise<{ data: CreateCheckoutResponse | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(
    'billing-create-checkout',
    { body: { project_id: projectId, plan, billing_cycle: billingCycle } },
  );

  if (error) return { data: null, error: error.message };
  if (isError(data)) return { data: null, error: data.error };
  return { data: data as CreateCheckoutResponse, error: null };
}

export async function manageSubscription(
  projectId: string,
  action: 'cancel' | 'update_payment',
): Promise<{ data: ManageSubscriptionResponse | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(
    'billing-create-portal',
    { body: { project_id: projectId, action } },
  );

  if (error) return { data: null, error: error.message };
  if (isError(data)) return { data: null, error: data.error };
  return { data: data as ManageSubscriptionResponse, error: null };
}

export async function getStatus(
  projectId: string,
): Promise<{ data: BillingStatusResponse | null; error: string | null }> {
  const { data, error } = await supabase.functions.invoke(
    'billing-status',
    { body: { project_id: projectId } },
  );

  if (error) return { data: null, error: error.message };
  if (isError(data)) return { data: null, error: data.error };
  return { data: data as BillingStatusResponse, error: null };
}
